import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DarkPage } from "@/components/app/PageHeader";
import { Headset, ShieldAlert, X, ChevronDown, Lock, Plus, Trash2, Pencil, PhoneCall } from "lucide-react";

const STATUS_STYLES = {
  open: "text-[#00F5D4] bg-[#00F5D4]/10 border-[#00F5D4]/20",
  pending: "text-amber-400 bg-amber-500/10 border-amber-500/20",
  resolved: "text-blue-400 bg-blue-500/10 border-blue-500/20",
  closed: "text-[#7d8590] bg-[#1c2128] border-[#30363d]",
};
const PRIORITY_STYLES = {
  low: "text-[#7d8590]", normal: "text-[#9CA3AF]", high: "text-amber-400", urgent: "text-rose-400",
};

const EMPTY_ARTICLE = { title: "", body: "", category: "General", is_published: true };

export default function SupportQueue() {
  const { user } = useAuth();
  const isAgent = user?.role === "superadmin";

  const [tab, setTab] = useState("queue");
  const [tickets, setTickets] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [ticketDetail, setTicketDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [asNote, setAsNote] = useState(false);
  const [busy, setBusy] = useState(false);

  const [articles, setArticles] = useState([]);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [articleForm, setArticleForm] = useState(EMPTY_ARTICLE);
  const [editingArticle, setEditingArticle] = useState(null);

  const loadTickets = useCallback(async () => {
    try {
      const params = statusFilter ? `?status=${statusFilter}` : "";
      const { data } = await api.get(`/help/tickets${params}`);
      setTickets(data || []);
    } catch {
      toast.error("Failed to load tickets");
    }
  }, [statusFilter]);

  const loadArticles = useCallback(async () => {
    try {
      const { data } = await api.get("/help/articles?include_unpublished=true");
      setArticles(data || []);
    } catch {
      toast.error("Failed to load articles");
    }
  }, []);

  useEffect(() => { if (tab === "queue") loadTickets(); }, [tab, loadTickets]);
  useEffect(() => { if (tab === "articles") loadArticles(); }, [tab, loadArticles]);

  const openTicket = async (t) => {
    try {
      const { data } = await api.get(`/help/tickets/${t.ticket_id}`);
      setTicketDetail(data);
    } catch {
      toast.error("Failed to load ticket");
    }
  };

  const refreshDetail = async () => {
    const { data } = await api.get(`/help/tickets/${ticketDetail.ticket_id}`);
    setTicketDetail(data);
  };

  const assignToMe = async () => {
    try {
      await api.patch(`/help/tickets/${ticketDetail.ticket_id}`, { assigned_to: user.user_id });
      toast.success("Assigned to you");
      refreshDetail();
      loadTickets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to assign");
    }
  };

  const setStatus = async (status) => {
    try {
      await api.patch(`/help/tickets/${ticketDetail.ticket_id}`, { status });
      refreshDetail();
      loadTickets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/help/tickets/${ticketDetail.ticket_id}/messages`, { body: reply, is_internal_note: asNote });
      setReply("");
      setAsNote(false);
      refreshDetail();
      loadTickets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally {
      setBusy(false);
    }
  };

  const saveArticle = async () => {
    if (!articleForm.title.trim() || !articleForm.body.trim()) return toast.error("Title and body are required");
    setBusy(true);
    try {
      if (editingArticle) {
        await api.put(`/help/articles/${editingArticle.article_id}`, articleForm);
        toast.success("Article updated");
      } else {
        await api.post("/help/articles", articleForm);
        toast.success("Article created");
      }
      setShowArticleModal(false);
      setEditingArticle(null);
      setArticleForm(EMPTY_ARTICLE);
      loadArticles();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save article");
    } finally {
      setBusy(false);
    }
  };

  const deleteArticle = async (article) => {
    if (!window.confirm(`Delete "${article.title}"?`)) return;
    try {
      await api.delete(`/help/articles/${article.article_id}`);
      toast.success("Deleted");
      loadArticles();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  if (!isAgent) {
    return (
      <div className="p-10 text-center text-sm text-[#7d8590] flex flex-col items-center gap-2">
        <ShieldAlert className="w-6 h-6" />
        Support queue is superadmin only.
      </div>
    );
  }

  if (ticketDetail) {
    return (
      <DarkPage><div className="p-6 max-w-2xl mx-auto">
        <button onClick={() => setTicketDetail(null)} className="text-xs text-[#7d8590] hover:text-white mb-4 flex items-center gap-1">
          <ChevronDown className="w-3 h-3 rotate-90" /> Back to queue
        </button>
        <div className="flex items-start justify-between mb-2">
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            {ticketDetail.channel === "live_support" && (
              <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-rose-500/30 text-rose-400 bg-rose-500/10">
                <PhoneCall className="w-3 h-3" /> Live
              </span>
            )}
            {ticketDetail.subject}
          </h1>
          <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLES[ticketDetail.status]}`}>{ticketDetail.status}</span>
        </div>
        <div className="text-xs text-[#7d8590] mb-4">
          {ticketDetail.requester_name} ({ticketDetail.requester_email}) · tenant {ticketDetail.tenant_id} · <span className={PRIORITY_STYLES[ticketDetail.priority]}>{ticketDetail.priority} priority</span>
        </div>
        <div className="flex gap-2 mb-4 flex-wrap">
          {!ticketDetail.assigned_to && (
            <button onClick={assignToMe} className="text-xs px-3 py-1.5 rounded-full border border-[#00F5D4]/30 text-[#00F5D4] bg-[#00F5D4]/10">Assign to me</button>
          )}
          {ticketDetail.assigned_to === user.user_id && <span className="text-xs px-3 py-1.5 rounded-full border border-[#30363d] text-[#7d8590]">Assigned to you</span>}
          {["open", "pending", "resolved", "closed"].filter(s => s !== ticketDetail.status).map(s => (
            <button key={s} onClick={() => setStatus(s)} className="text-xs px-3 py-1.5 rounded-full border border-[#30363d] text-[#7d8590] hover:text-white capitalize">
              Mark {s}
            </button>
          ))}
        </div>
        <div className="space-y-3 mb-4">
          {ticketDetail.messages.map(m => (
            <div key={m.message_id} className={`border rounded-lg p-4 ${m.is_internal_note ? "bg-amber-500/5 border-amber-500/20" : "bg-[#0d1117] border-[#21262d]"}`}>
              <div className="text-xs text-[#7d8590] mb-1 flex items-center gap-1.5">
                <span className="text-white font-medium">{m.author_name}</span> · {new Date(m.created_at).toLocaleString()}
                {m.is_internal_note && <span className="flex items-center gap-1 text-amber-400"><Lock className="w-3 h-3" /> internal note</span>}
              </div>
              <div className="text-sm text-[#c9d1d9] whitespace-pre-wrap">{m.body}</div>
            </div>
          ))}
        </div>
        <div className="space-y-2">
          <textarea value={reply} onChange={e => setReply(e.target.value)} rows={3} placeholder="Reply to the customer, or check 'internal note' to leave a note only your team sees…"
            className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm resize-none" />
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs text-[#7d8590]">
              <input type="checkbox" checked={asNote} onChange={e => setAsNote(e.target.checked)} />
              Internal note (not visible to customer)
            </label>
            <Button onClick={sendReply} disabled={busy} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
              {busy ? "Sending…" : asNote ? "Add note" : "Send reply"}
            </Button>
          </div>
        </div>
      </div></DarkPage>
    );
  }

  return (
    <DarkPage><div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Headset className="w-5 h-5 text-[#7d8590]" /> Support Queue</h1>
        <p className="text-sm text-[#7d8590] mt-1">Cross-tenant ticket queue and Help Center content.</p>
      </div>

      <div className="flex bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden w-fit mb-6">
        {[["queue", "Ticket queue"], ["articles", "Articles"]].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm transition-colors ${tab === key ? "bg-[#1c2128] text-white font-medium" : "text-[#7d8590] hover:text-white"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "queue" ? (
        <>
          <div className="flex gap-2 mb-4">
            {["", "open", "pending", "resolved", "closed"].map(s => (
              <button key={s || "all"} onClick={() => setStatusFilter(s)}
                className={`text-xs px-3 py-1.5 rounded-full border capitalize ${statusFilter === s ? "border-[#00F5D4]/30 text-[#00F5D4] bg-[#00F5D4]/10" : "border-[#30363d] text-[#7d8590] hover:text-white"}`}>
                {s || "All"}
              </button>
            ))}
          </div>
          {tickets.length === 0 ? (
            <div className="text-center py-16 text-[#7d8590] text-sm">No tickets.</div>
          ) : (
            <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden">
              {tickets.map(t => (
                <button key={t.ticket_id} onClick={() => openTicket(t)}
                  className="w-full text-left flex items-center justify-between px-4 py-3 border-b border-[#21262d] last:border-0 hover:bg-[#161b22] transition-colors">
                  <div>
                    <div className="text-sm text-white flex items-center gap-2">
                      {t.channel === "live_support" && (
                        <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border border-rose-500/30 text-rose-400 bg-rose-500/10 shrink-0">
                          <PhoneCall className="w-2.5 h-2.5" /> Live
                        </span>
                      )}
                      {t.subject}
                    </div>
                    <div className="text-xs text-[#7d8590] mt-0.5">{t.requester_name} · tenant {t.tenant_id} · <span className={PRIORITY_STYLES[t.priority]}>{t.priority}</span>{!t.assigned_to && " · unassigned"}</div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLES[t.status]}`}>{t.status}</span>
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="flex justify-end mb-4">
            <Button onClick={() => { setEditingArticle(null); setArticleForm(EMPTY_ARTICLE); setShowArticleModal(true); }} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
              <Plus className="w-4 h-4 mr-1" /> New article
            </Button>
          </div>
          <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden">
            {articles.map(a => (
              <div key={a.article_id} className="flex items-center justify-between px-4 py-3 border-b border-[#21262d] last:border-0 group">
                <div>
                  <div className="text-sm text-white">{a.title}</div>
                  <div className="text-xs text-[#7d8590] mt-0.5">{a.category} · {a.view_count || 0} views {!a.is_published && "· draft"}</div>
                </div>
                <div className="flex gap-2 opacity-0 group-hover:opacity-100">
                  <button onClick={() => { setEditingArticle(a); setArticleForm({ title: a.title, body: a.body, category: a.category, is_published: a.is_published }); setShowArticleModal(true); }} className="text-[#7d8590] hover:text-white"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => deleteArticle(a)} className="text-[#484f58] hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
            {articles.length === 0 && <div className="px-4 py-8 text-center text-sm text-[#7d8590]">No articles yet.</div>}
          </div>
        </>
      )}

      {showArticleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 overflow-y-auto py-8">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">{editingArticle ? "Edit article" : "New article"}</h2>
              <button onClick={() => setShowArticleModal(false)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Title *</label>
                <input value={articleForm.title} onChange={e => setArticleForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Category</label>
                <input value={articleForm.category} onChange={e => setArticleForm(f => ({ ...f, category: e.target.value }))}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Body *</label>
                <textarea value={articleForm.body} onChange={e => setArticleForm(f => ({ ...f, body: e.target.value }))} rows={8}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm resize-none font-mono" />
              </div>
              <label className="flex items-center gap-2 text-xs text-[#7d8590]">
                <input type="checkbox" checked={articleForm.is_published} onChange={e => setArticleForm(f => ({ ...f, is_published: e.target.checked }))} />
                Published (visible to all workspaces)
              </label>
            </div>
            <div className="flex gap-2 mt-5">
              <Button onClick={saveArticle} disabled={busy} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
                {busy ? "Saving…" : editingArticle ? "Save changes" : "Create article"}
              </Button>
              <Button onClick={() => setShowArticleModal(false)} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div></DarkPage>
  );
}
